import { observeApiResponses, type ObservedApiResponse } from "@/api/responseObserver";
import { type Models, ShopDisplayType } from "@/api/types";
import { adPixels, adPrice } from "@/modules/analytics/AdPixels/adPixels";

import { reportCartChange } from "./appClientActivity";
import { ClientActivityType } from "./types";

type CartLine = Pick<
  Models<"ShopDisplayOrderLineItemModel">,
  | "id"
  | "shopDisplayId"
  | "displayType"
  | "name"
  | "productName"
  | "quantity"
  | "packagePriceId"
  | "package"
  | "cost"
>;

const CART_ENDPOINT = /\/api\/v1\/orders\/current\/(details|lineitems)(?:\/([^/?#]+))?(?:[?#].*)?$/;

const NAME_PROPERTY = "name";
const QUANTITY_PROPERTY = "quantity";
const PRODUCT_LISTING_ID_PROPERTY = "retailProductListingId";
const PACKAGE_LISTING_ID_PROPERTY = "packageListingId";
const MEMBERSHIP_ID_PROPERTY = "membershipId";

// The cart lines the patient app last saw: the delete and edit responses no longer carry the
// line they removed or replaced, so what it was comes from here, not from another request.
let knownLines = new Map<string, CartLine>();

const isRecord = (value: unknown): value is Record<string, unknown> =>
  value !== null && typeof value === "object";

const isCartLine = (value: unknown): value is CartLine =>
  isRecord(value) &&
  typeof value.id === "string" &&
  typeof value.shopDisplayId === "string" &&
  typeof value.displayType === "number" &&
  typeof value.quantity === "number";

/** The line items of an order response, which the API sends bare or inside its `result` envelope. */
function linesOf(responseBody: unknown): CartLine[] | undefined {
  const order =
    isRecord(responseBody) && "result" in responseBody ? responseBody.result : responseBody;
  if (!isRecord(order) || !Array.isArray(order.shopDisplayOrderLineItems)) return undefined;
  return order.shopDisplayOrderLineItems.filter(isCartLine);
}

function idProperty(displayType: ShopDisplayType): string {
  switch (displayType) {
    case ShopDisplayType.Membership:
      return MEMBERSHIP_ID_PROPERTY;
    case ShopDisplayType.Product:
    case ShopDisplayType.ProductFromExternalSource:
      return PRODUCT_LISTING_ID_PROPERTY;
    default:
      return PACKAGE_LISTING_ID_PROPERTY;
  }
}

function cartChangeProperties(line: CartLine, quantity: number): Record<string, string> {
  const name = line.productName || line.name;
  return {
    ...(name ? { [NAME_PROPERTY]: name } : {}),
    [QUANTITY_PROPERTY]: String(quantity),
    [idProperty(line.displayType)]: line.shopDisplayId,
  };
}

function reportAdded(line: CartLine, quantity: number): void {
  if (quantity > 0) {
    reportCartChange(ClientActivityType.CartAdd, cartChangeProperties(line, quantity));
    adPixels.addToCart(
      {
        id: line.shopDisplayId,
        name: line.productName || line.name || undefined,
        // A line's cost is the whole line's, so the added share is priced pro rata.
        ...(typeof line.cost === "number" &&
          line.quantity > 0 &&
          adPrice(Math.round((line.cost * quantity) / line.quantity))),
      },
      quantity,
    );
  }
}

function reportRemoved(line: CartLine, quantity: number): void {
  if (quantity > 0) {
    reportCartChange(ClientActivityType.CartRemove, cartChangeProperties(line, quantity));
  }
}

/**
 * What one add request put in the cart. Only product lines merge into an existing line, and they
 * grow by the price option's quantity (or the requested units for a one-unit price), the rule the
 * cart applies. A line with no price option was not merged, so its quantity is the add.
 */
function addedQuantity(line: CartLine, requestedQuantity: unknown): number {
  const option = line.package?.packagePrices.find(({ id }) => id === line.packagePriceId);
  if (option === undefined) return line.quantity;
  return typeof requestedQuantity === "number" && option.quantity === 1
    ? requestedQuantity
    : option.quantity;
}

function reportAddRequest(request: unknown, lines: readonly CartLine[]): void {
  if (!isRecord(request)) return;
  const { membershipId, packagePriceId, quantity } = request;

  if (typeof membershipId === "string") {
    const membership = lines.find(
      (line) =>
        line.displayType === ShopDisplayType.Membership && line.shopDisplayId === membershipId,
    );
    if (membership !== undefined) reportAdded(membership, membership.quantity);
  }
  if (typeof packagePriceId === "string") {
    const added = lines.find((line) => line.packagePriceId === packagePriceId);
    if (added !== undefined) reportAdded(added, addedQuantity(added, quantity));
  }
}

function reportEditedLine(lineId: string, lines: readonly CartLine[]): void {
  const before = knownLines.get(lineId);
  const after = lines.find((line) => line.id === lineId);
  if (before === undefined || after === undefined) return;
  const change = after.quantity - before.quantity;
  if (change > 0) reportAdded(after, change);
  else reportRemoved(after, -change);
}

/**
 * Turns one successful cart API response into client activity. Run for every response, it covers
 * each caller of the cart endpoints, legacy or generated, and only after the server accepted the
 * change.
 */
export function observeCartResponse({
  method,
  url,
  requestBody,
  responseBody,
}: ObservedApiResponse): void {
  const match = CART_ENDPOINT.exec(url);
  if (match === null) return;
  const [, endpoint, lineId] = match;

  if (endpoint === "lineitems" && method === "DELETE" && lineId !== undefined) {
    const removed = knownLines.get(lineId);
    if (removed !== undefined) {
      reportRemoved(removed, removed.quantity);
      knownLines.delete(lineId);
    }
    return;
  }

  const isCartRead = endpoint === "details" && method === "GET";
  const isAdd = endpoint === "lineitems" && method === "POST" && lineId === undefined;
  const isEdit = endpoint === "lineitems" && method === "PUT" && lineId !== undefined;
  if (!isCartRead && !isAdd && !isEdit) return;

  const lines = linesOf(responseBody);
  if (lines === undefined) return;

  if (isAdd) reportAddRequest(requestBody, lines);
  if (isEdit) reportEditedLine(lineId, lines);
  knownLines = new Map(lines.map((line) => [line.id, line]));
}

/** Starts reporting cart adds and removes. Calling it again does nothing. */
export function trackCartActivity(): void {
  observeApiResponses(observeCartResponse);
}
